import { useEffect, useMemo, useRef, useState } from 'react';
import './App.css';

const navLinks = [
  { label: 'Home', href: '/', icon: 'home' },
  { label: 'About', href: '/about', icon: 'about' },
  { label: 'Services', href: '/services', icon: 'services' },
  { label: 'Markets', href: '/markets', icon: 'markets' },
  { label: 'Contact', href: '/contact', icon: 'contact' },
];

const routePages = {
  '/': 'home',
  '/about': 'about',
  '/services': 'services',
  '/markets': 'markets',
  '/contact': 'contact',
};

const hashRoutes = {
  '#home': '/',
  '#about': '/about',
  '#services': '/services',
  '#markets': '/markets',
  '#contact': '/contact',
  '#why-choose': '/',
  '#commitment': '/',
};

function normalizePath(path) {
  const cleanPath = (path || '/').split('#')[0].replace(/\/+$/, '') || '/';
  return routePages[cleanPath] ? cleanPath : '/';
}

function slugifyLabel(label) {
  return label
    .toLowerCase()
    .replace(/&/g, 'and')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

function getCurrentPath() {
  if (typeof window === 'undefined') {
    return '/';
  }

  return hashRoutes[window.location.hash] || normalizePath(window.location.pathname);
}

const themeOptions = [
  { name: 'Deep forest', key: 'forest', color: '#063B2B' },
  { name: 'Emerald', key: 'emerald', color: '#0F6B4A' },
  { name: 'Olive green', key: 'olive', color: '#567833' },
];

const values = [
  {
    title: 'Integrity',
    letter: 'I',
    icon: 'shield',
    text: 'Acting with honesty, transparency, and professionalism at all times.',
  },
  {
    title: 'Stewardship',
    letter: 'S',
    icon: 'stewardship',
    text: 'Managing resources responsibly and with accountability.',
  },
  {
    title: 'Excellence',
    letter: 'E',
    icon: 'award',
    text: 'Delivering accurate, high-quality work that meets professional standards.',
  },
  {
    title: 'Wisdom',
    letter: 'W',
    icon: 'lightbulb',
    text: 'Providing insight and guidance beyond the numbers.',
  },
  {
    title: 'Harmony',
    letter: 'H',
    icon: 'harmony',
    text: 'Promoting balance, order, and compliance in financial affairs.',
  },
  {
    title: 'Partnership',
    letter: 'P',
    icon: 'handshake',
    text: 'Building trusted, long-term relationships with our clients.',
  },
];

const services = [
  {
    title: 'Financial Consulting',
    icon: 'chart',
    summary: 'Reliable planning, reporting, analysis, and control support for stronger financial decisions.',
    items: [
      'Preparation of financial statements in accordance with IFRS and local regulatory requirements',
      'Management accounts and financial performance analysis',
      'Cash flow forecasting and business planning',
      'Budgeting and variance analysis',
      'Review and improvement of financial systems and internal controls',
      'Design and implementation of internal controls',
    ],
  },
  {
    title: 'Audit Preparation & Support',
    icon: 'audit',
    summary: 'Pre-audit assessment support that helps teams prepare clean records and documentation.',
    items: [
      'Review of accounting records prior to external audits',
      'Audit readiness assessments and documentation support',
      'Identification of potential audit findings and corrective guidance',
      'Liaison and coordination with external auditors to ensure a smooth audit process',
    ],
  },
  {
    title: 'Tax & Compliance',
    icon: 'tax',
    summary: 'Tax planning, return support, clearance assistance, and compliance advisory.',
    items: [
      'Preparation of corporate and individual tax computations and return submissions',
      'Tax planning and advisory services',
      'Preparation and review of VAT and PAYE returns',
      'Tax clearance assistance and compliance advisory',
      'Liaison with Botswana Unified Revenue Service (BURS) on any tax disputes',
    ],
  },
  {
    title: 'Payroll Management',
    icon: 'payroll',
    summary: 'Accurate payroll processing, PAYE administration, and employee pay record support.',
    items: [
      'Monthly payroll processing and payroll register preparation',
      'PAYE calculations, statutory deductions, and return support',
      'Payslip preparation and employee payroll records management',
      'Leave, overtime, and salary adjustment coordination',
      'Payroll reconciliations and management reporting support',
      'Setup and review of payroll procedures for compliance and accuracy',
    ],
  },
  {
    title: 'Internal Controls & Risk Advisory',
    icon: 'risk',
    summary: 'Control frameworks, risk identification, and practical process improvement.',
    items: [
      'Assessment and review of internal control frameworks',
      'Identification of financial, operational, and compliance risks',
      'Development of internal control policies and procedures',
      'Segregation of duties and process improvement reviews',
      'Fraud risk assessment and control enhancement',
    ],
  },
  {
    title: 'Financial Training & Capacity Building',
    icon: 'training',
    summary: 'Plain-language financial training for leaders, entrepreneurs, and operational teams.',
    items: [
      'Training for non-financial managers and entrepreneurs on financial statement interpretation',
      'Workshops on cash flow preparation and analysis',
      'SME-focused training on record-keeping, budgeting, and compliance essentials',
      'Tailored in-house or group training programs',
    ],
  },
  {
    title: 'Company Secretarial Services',
    icon: 'document',
    summary: 'Registration, board secretarial, compliance, and governance documentation support.',
    items: [
      'Company registrations and statutory amendments',
      'Board secretarial services',
      'Preparation and updating of company constitutions',
      'Annual returns filing and compliance monitoring',
      'Corporate governance documentation and advisory',
    ],
  },
];

const mobileMenuSections = {
  '/about': [
    { label: 'Overview', href: '/about#about-overview', icon: 'about' },
    { label: 'Vision & Mission', href: '/about#vision-mission', icon: 'vision' },
    { label: 'Core Values', href: '/about#core-values', icon: 'values' },
  ],
  '/services': services.map((service) => ({
    label: service.title,
    href: `/services#service-${slugifyLabel(service.title)}`,
    icon: service.icon,
  })),
};

const whyChoose = [
  {
    title: 'Delivering financial clarity for confident decision-making',
    text: 'We convert financial information into clear, actionable insight that supports sound leadership and governance.',
  },
  {
    title: 'Supporting sustainable short- and long-term financial strategies',
    text: 'Our approach balances immediate financial stability with forward-looking structures that enable responsible growth.',
  },
  {
    title: 'Strengthening stewardship, discipline, and control',
    text: 'We help organizations establish robust financial systems and controls that reduce risk and enhance accountability.',
  },
  {
    title: 'Positioning organizations for scrutiny and growth',
    text: 'Clients are prepared to meet audit, regulatory, and stakeholder expectations while remaining growth ready.',
  },
  {
    title: 'Partnering beyond compliance',
    text: 'We work alongside leadership teams as trusted advisors, supporting informed decisions and long-term value creation.',
  },
];

const markets = [
  'Small and medium-sized enterprises preparing for external audits',
  'Start-ups and growing businesses seeking professional accounting set-up and advisory support',
  'Non-governmental organisations and donor-funded projects requiring compliance and reporting support',
  'Growing employers needing reliable payroll processing, PAYE support, and stronger financial controls',
  'Corporates seeking payroll support and financial training for management and operational teams',
];

const commitments = [
  'We are committed to maintaining confidentiality, upholding integrity, and delivering timely, value-driven solutions that support informed decision-making and long-term success.',
  'We are committed to fostering transparent communication, providing proactive guidance, and building enduring partnerships founded on trust, accountability, and measurable results.',
];

const homeMetrics = [
  { value: '7', label: 'Core service areas' },
  { value: 'IFRS', label: 'Reporting discipline' },
  { value: 'BURS', label: 'Tax compliance support' },
  { value: 'Gaborone', label: 'Local advisory presence' },
];

const advisoryProcess = [
  {
    title: 'Understand the records',
    text: 'We review the structure, source documents, reporting needs, and current compliance position before recommending action.',
    icon: 'search',
  },
  {
    title: 'Build the plan',
    text: 'The work is organised into clear priorities across tax, payroll, audit readiness, controls, training, reporting, and governance.',
    icon: 'document',
  },
  {
    title: 'Strengthen the system',
    text: 'Clients receive practical support that improves accountability, prepares teams for scrutiny, and supports better decisions.',
    icon: 'shield',
  },
];

const pageSummaries = [
  {
    title: 'About Havillah Harmony',
    href: '/about',
    text: 'Professional accounting and advisory services for reliable, ethical, and insightful financial support.',
  },
  {
    title: 'Vision & Mission',
    href: '/about',
    text: 'Financial clarity, stewardship, integrity, excellence, harmony, balance, and sustainable growth.',
  },
  {
    title: 'Core Values',
    href: '/about',
    text: values.map((value) => `${value.title} ${value.text}`).join(' '),
  },
  {
    title: 'Services',
    href: '/services',
    text: services.map((service) => `${service.title} ${service.summary} ${service.items.join(' ')}`).join(' '),
  },
  {
    title: 'Why Choose Us',
    href: '/',
    text: [
      ...whyChoose.map((item) => `${item.title} ${item.text}`),
      ...advisoryProcess.map((item) => `${item.title} ${item.text}`),
    ].join(' '),
  },
  {
    title: 'Target Market',
    href: '/markets',
    text: markets.join(' '),
  },
  {
    title: 'Client Commitment',
    href: '/',
    text: commitments.join(' '),
  },
  {
    title: 'Contact',
    href: '/contact',
    text: 'Plot 63295, Tsholofelo West, Botswana +267 74779425 +267 71573465 info@havillahharmony.co.bw cmakunike@havillahharmony.co.bw gmakunike@havillahharmony.co.bw',
  },
];

const contact = {
  address: 'Plot 63295, Tsholofelo West, Botswana',
  phones: ['+267 74779425', '+267 71573465'],
  emails: [
    'cmakunike@havillahharmony.co.bw',
    'gmakunike@havillahharmony.co.bw',
    'info@havillahharmony.co.bw',
  ],
};

const contactFormInitialState = {
  name: '',
  email: '',
  phone: '',
  service: 'General enquiry',
  message: '',
};
const contactMapQuery = encodeURIComponent(contact.address);
const contactMapSrc = `https://www.google.com/maps?q=${contactMapQuery}&output=embed`;
const contactMapLink = `https://www.google.com/maps/search/?api=1&query=${contactMapQuery}`;
const WHATSAPP_AGENT_PHONE = '+26774779425';
const WHATSAPP_AGENT_URL = `https://wa.me/${WHATSAPP_AGENT_PHONE.replace(/\D/g, '')}?text=${encodeURIComponent(
  `Hello Havillah Harmony, I need help from a live agent on ${WHATSAPP_AGENT_PHONE}.`,
)}`;
const THINKING_DELAY_MS = 3000;
const TYPEWRITER_SPEED_MS = 16;
const PAGE_LOADER_MIN_MS = 650;
const SCROLL_UP_REVEAL_OFFSET = 260;
const pageHeroes = {
  about: {
    eyebrow: 'About Havillah Harmony',
    title: 'Guidance built on stewardship and clarity.',
    text: 'Reliable, ethical, and insightful accounting support shaped around integrity, excellence, harmony, and long-term value creation.',
    image: '/havillah/about-team.webp',
    alt: 'Black accountant reviewing financial records at her desk',
    cta: { label: 'Meet the firm', href: '/about', icon: 'about' },
    secondaryCta: { label: 'Our values', href: '/about', icon: 'values' },
    highlights: ['Integrity', 'Stewardship', 'Partnership'],
  },
  services: {
    eyebrow: 'Services',
    title: 'Accounting support for confident decisions.',
    text: 'Financial consulting, payroll management, audit preparation, tax compliance, internal controls, training, and company secretarial services for organised growth.',
    image: '/havillah/service-consulting.webp',
    alt: 'Black financial professionals reviewing advisory reports together',
    cta: { label: 'View services', href: '/services', icon: 'services' },
    secondaryCta: { label: 'Book consultation', href: '/contact', icon: 'calendar' },
    highlights: ['Payroll Management', 'Tax & Compliance', 'Audit Readiness', 'Financial Training'],
  },
  markets: {
    eyebrow: 'Target Markets',
    title: 'Built for organisations that need clarity.',
    text: 'Support for SMEs, start-ups, NGOs, donor-funded projects, and corporate teams seeking reporting discipline, payroll structure, and financial training.',
    image: '/havillah/target-market.webp',
    alt: 'Black business team planning growth around financial reports',
    cta: { label: 'Who we support', href: '/markets', icon: 'markets' },
    secondaryCta: { label: 'Discuss your needs', href: '/contact', icon: 'contact' },
    highlights: ['SMEs', 'Start-ups', 'NGOs', 'Corporate Teams'],
  },
  contact: {
    eyebrow: 'Contact',
    title: 'Start the conversation with Havillah Harmony.',
    text: 'Reach the team in Gaborone for accounting, payroll, tax, audit readiness, compliance, training, and advisory support.',
    image: '/havillah/contact-tower.webp',
    alt: 'Black professional preparing notes for a financial consultation',
    cta: { label: 'Email the team', href: `mailto:${contact.emails[2]}`, icon: 'email' },
    secondaryCta: { label: 'WhatsApp support', href: WHATSAPP_AGENT_URL, icon: 'whatsapp', external: true },
    highlights: ['Gaborone', '+267 74779425', 'info@havillahharmony.co.bw'],
  },
};
const QUICK_CHAT_ACTIONS = [
  {
    id: 'tax',
    icon: 'tax',
    label: { en: 'Tax services', tn: 'Lekgetho' },
    prompt: { en: 'Tell me about tax and BURS support.', tn: 'Botsa ka lekgetho le BURS support.' },
  },
  {
    id: 'payroll',
    icon: 'payroll',
    label: { en: 'Payroll', tn: 'Payroll' },
    prompt: { en: 'Tell me about payroll management.', tn: 'Botsa ka payroll management.' },
  },
  {
    id: 'audit',
    icon: 'audit',
    label: { en: 'Audit help', tn: 'Audit help' },
    prompt: { en: 'How can you help with audit preparation?', tn: 'Lo thusa jang ka audit preparation?' },
  },
  {
    id: 'training',
    icon: 'training',
    label: { en: 'Training', tn: 'Thupiso' },
    prompt: { en: 'Tell me about financial training.', tn: 'Botsa ka thupiso ya financial training.' },
  },
  {
    id: 'markets',
    icon: 'markets',
    label: { en: 'Markets', tn: 'Markets' },
    prompt: { en: 'Which organisations do you support?', tn: 'Lo thusa organisations dife mo target markets?' },
  },
  {
    id: 'contact',
    icon: 'phone',
    label: { en: 'Contact details', tn: 'Kgolagano' },
    prompt: { en: 'Share contact details.', tn: 'Mphe dintlha tsa kgolagano.' },
  },
];

function botCopy(en, tn, known = true) {
  return { en, tn, known };
}

function formatBotMessage(reply, language = 'en') {
  return reply[language] || reply.en;
}

function Icon({ name, className = '' }) {
  const icons = {
    about: (
      <path d="M12 12.5a4 4 0 1 0-4-4 4 4 0 0 0 4 4Zm7 7a7 7 0 0 0-14 0" />
    ),
    'arrow-up': <path d="m5 12 7-7 7 7M12 5v14" />,
    audit: (
      <>
        <path d="M8 3h8l3 3v15H5V3h3Z" />
        <path d="M15 3v4h4M8 12h8M8 16h5M8 8h3" />
      </>
    ),
    award: (
      <>
        <path d="M8 8a4 4 0 1 1 8 0 4 4 0 0 1-8 0Z" />
        <path d="m9.5 12-1.2 8 3.7-2 3.7 2-1.2-8" />
      </>
    ),
    calendar: (
      <>
        <path d="M5 5h14v15H5zM8 3v4M16 3v4M5 10h14" />
        <path d="M9 14h2M13 14h2M9 17h2" />
      </>
    ),
    chart: (
      <>
        <path d="M4 19h16M7 16V9M12 16V5M17 16v-4" />
        <path d="m6 10 6-5 5 7" />
      </>
    ),
    chat: (
      <>
        <path d="M5 6h14v9H9l-4 4V6Z" />
        <path d="M8 10h8M8 13h5" />
      </>
    ),
    'chevron-down': <path d="m6 9 6 6 6-6" />,
    close: <path d="m7 7 10 10M17 7 7 17" />,
    contact: (
      <>
        <path d="M5 7h14v10H5z" />
        <path d="m5 8 7 5 7-5" />
      </>
    ),
    document: (
      <>
        <path d="M7 3h7l4 4v14H7z" />
        <path d="M14 3v5h4M10 12h5M10 16h5" />
      </>
    ),
    email: (
      <>
        <path d="M4 6h16v12H4z" />
        <path d="m4 7 8 6 8-6" />
      </>
    ),
    facebook: (
      <>
        <path
          data-fill-icon
          d="M13.6 21.5v-7.25h2.43l.37-2.82h-2.8v-1.8c0-.82.23-1.38 1.4-1.38h1.5V5.73c-.72-.1-1.45-.15-2.18-.15-2.17 0-3.65 1.33-3.65 3.75v2.1H8.23v2.82h2.44v7.25h2.93Z"
        />
      </>
    ),
    handshake: (
      <>
        <path d="m7 12 3-3 3 3 2-2 4 4-4 4-5-5-3 3-3-3 3-3Z" />
        <path d="m14 16 2-2M11 13l-2 2" />
      </>
    ),
    harmony: (
      <>
        <path d="M4 12a8 8 0 0 1 14-5" />
        <path d="M20 12a8 8 0 0 1-14 5" />
        <path d="M18 3v4h-4M6 21v-4h4" />
      </>
    ),
    home: (
      <>
        <path d="m4 11 8-7 8 7" />
        <path d="M6 10v10h12V10M10 20v-6h4v6" />
      </>
    ),
    instagram: (
      <>
        <rect x="5" y="5" width="14" height="14" rx="4" />
        <path d="M9 12a3 3 0 1 0 6 0 3 3 0 0 0-6 0ZM16.5 7.5h.01" />
      </>
    ),
    lightbulb: (
      <>
        <path d="M9 18h6M10 21h4M8 11a4 4 0 1 1 8 0c0 2-2 3-2 5h-4c0-2-2-3-2-5Z" />
      </>
    ),
    linkedin: (
      <>
        <path
          data-fill-icon
          d="M5.15 8.75h3.18v10.1H5.15V8.75Zm1.6-4.9a1.84 1.84 0 1 1 0 3.68 1.84 1.84 0 0 1 0-3.68Zm3.85 4.9h3.04v1.38h.04c.43-.8 1.47-1.64 3.02-1.64 3.23 0 3.83 2.12 3.83 4.88v5.48h-3.17v-4.86c0-1.16-.02-2.65-1.62-2.65-1.62 0-1.87 1.26-1.87 2.56v4.95H10.6V8.75Z"
        />
      </>
    ),
    location: (
      <>
        <path d="M12 21s7-5 7-11a7 7 0 0 0-14 0c0 6 7 11 7 11Z" />
        <path d="M9.5 10a2.5 2.5 0 1 0 5 0 2.5 2.5 0 0 0-5 0Z" />
      </>
    ),
    menu: (
      <>
        <path d="M4 6h16" />
        <path d="M4 12h16" />
        <path d="M4 18h16" />
      </>
    ),
    markets: (
      <>
        <path d="M4 19V7l8-4 8 4v12" />
        <path d="M8 19v-6h3v6M13 19v-9h3v9M4 19h16" />
      </>
    ),
    moon: <path d="M20 15.5A8.5 8.5 0 0 1 8.5 4 7 7 0 1 0 20 15.5Z" />,
    phone: (
      <path d="M7 5h4l1 4-2 1a11 11 0 0 0 5 5l1-2 4 1v4c0 1-1 2-2 2A14 14 0 0 1 5 7c0-1 1-2 2-2Z" />
    ),
    payroll: (
      <>
        <path d="M3 7h18v10H3z" />
        <path d="M3 10h18" />
        <path d="M7 14h3M13 14h4" />
      </>
    ),
    risk: (
      <>
        <path d="M12 3 4 7v5c0 5 3.5 8 8 9 4.5-1 8-4 8-9V7l-8-4Z" />
        <path d="M12 8v5M12 16h.01" />
      </>
    ),
    search: (
      <>
        <path d="M10.5 18a7.5 7.5 0 1 1 0-15 7.5 7.5 0 0 1 0 15Z" />
        <path d="m16 16 5 5" />
      </>
    ),
    services: (
      <>
        <path d="M5 7h14M5 12h14M5 17h14" />
        <path d="M8 4v16M16 4v16" />
      </>
    ),
    shield: (
      <>
        <path d="M12 3 5 6v5c0 5 3 8 7 10 4-2 7-5 7-10V6l-7-3Z" />
        <path d="m9 12 2 2 4-5" />
      </>
    ),
    stewardship: (
      <>
        <path d="M5 14c4-6 9-8 14-8 0 7-4 12-11 12" />
        <path d="M5 14c2 0 4 1 6 4M7 17l-3 3" />
      </>
    ),
    sun: (
      <>
        <path d="M12 7a5 5 0 1 0 0 10 5 5 0 0 0 0-10Z" />
        <path d="M12 2v2M12 20v2M4 12H2M22 12h-2M5 5l1.5 1.5M17.5 17.5 19 19M19 5l-1.5 1.5M6.5 17.5 5 19" />
      </>
    ),
    tax: (
      <>
        <path d="M5 5h14v14H5z" />
        <path d="m8 16 8-8M8 8h.01M16 16h.01" />
      </>
    ),
    training: (
      <>
        <path d="M4 6h16v10H4zM8 20h8M12 16v4" />
        <path d="m8 12 3-3 2 2 3-4" />
      </>
    ),
    values: (
      <>
        <path d="M12 3 4 8l8 5 8-5-8-5Z" />
        <path d="m4 13 8 5 8-5" />
      </>
    ),
    vision: (
      <>
        <path d="M3 12s3.5-6 9-6 9 6 9 6-3.5 6-9 6-9-6-9-6Z" />
        <path d="M9 12a3 3 0 1 0 6 0 3 3 0 0 0-6 0Z" />
      </>
    ),
    whatsapp: (
      <>
        <path
          data-fill-icon
          fillRule="evenodd"
          d="M12.04 2.85a8.98 8.98 0 0 0-7.71 13.58l-1.08 4.72 4.84-1.1a8.9 8.9 0 0 0 3.94.91h.01a9.06 9.06 0 0 0 0-18.11Zm.01 1.72a7.33 7.33 0 0 1-.01 14.66h-.01a7.2 7.2 0 0 1-3.55-.92l-.34-.2-2.15.49.48-2.1-.22-.35a7.24 7.24 0 0 1-1.13-3.9 7.35 7.35 0 0 1 6.93-7.68Zm-3.3 3.62c-.16 0-.43.06-.65.31-.22.24-.85.82-.85 2.02 0 1.19.87 2.34.99 2.5.12.16 1.68 2.68 4.18 3.66 2.07.81 2.5.65 2.95.6.46-.04 1.48-.6 1.69-1.19.2-.58.2-1.08.14-1.19-.07-.11-.23-.18-.48-.3-.25-.13-1.48-.73-1.71-.81-.23-.09-.39-.13-.56.12-.17.25-.65.81-.8.97-.15.17-.29.19-.54.07-.26-.13-1.07-.4-2.04-1.26-.75-.67-1.26-1.49-1.4-1.75-.15-.25-.02-.39.11-.52.11-.11.25-.29.37-.43.13-.14.17-.25.25-.42.09-.16.04-.31-.02-.43-.06-.13-.56-1.36-.77-1.86-.2-.48-.4-.49-.56-.5l-.49-.01Z"
        />
      </>
    ),
  };

  return (
    <svg className={`icon ${className}`.trim()} aria-hidden="true" viewBox="0 0 24 24">
      {icons[name]}
    </svg>
  );
}

function getBotReply(message) {
  const lowerMessage = message.toLowerCase();

  if (
    lowerMessage.includes('payroll')
    || lowerMessage.includes('salary')
    || lowerMessage.includes('salaries')
    || lowerMessage.includes('wage')
    || lowerMessage.includes('wages')
    || lowerMessage.includes('payslip')
  ) {
    return botCopy(
      'Payroll management support covers payroll processing, PAYE calculations, payslips, payroll reconciliations, employee pay records, and payroll compliance guidance.',
      'Payroll management support e akaretsa payroll processing, PAYE calculations, payslips, payroll reconciliations, employee pay records, le kgakololo ya payroll compliance.',
    );
  }

  if (
    lowerMessage.includes('tax')
    || lowerMessage.includes('burs')
    || lowerMessage.includes('vat')
    || lowerMessage.includes('paye')
    || lowerMessage.includes('lekgetho')
  ) {
    return botCopy(
      'For tax support, Havillah Harmony can help with tax computations, VAT and PAYE returns, tax clearance assistance, compliance advisory, and BURS liaison.',
      'Mo thusong ya lekgetho, Havillah Harmony e ka thusa ka dipalo tsa lekgetho, VAT le PAYE returns, tax clearance, kgakololo ya compliance, le puisano le BURS.',
    );
  }

  if (lowerMessage.includes('audit')) {
    return botCopy(
      'For audit preparation, the team reviews accounting records, checks audit readiness, supports documentation, and coordinates with external auditors.',
      'Mo boitokisetsong jwa audit, setlhopha se sekaseka direkoto tsa accounting, se lebelela gore lo ipaakantse, se thusa ka ditokomane, mme se buisana le external auditors.',
    );
  }

  if (lowerMessage.includes('training') || lowerMessage.includes('capacity') || lowerMessage.includes('thupiso')) {
    return botCopy(
      'Training support covers financial statement interpretation, cash flow preparation, record-keeping, budgeting, and compliance essentials.',
      'Thupiso e akaretsa go tlhaloganya financial statements, go dira cash flow, record-keeping, budgeting, le dintlha tsa botlhokwa tsa compliance.',
    );
  }

  if (
    lowerMessage.includes('service')
    || lowerMessage.includes('ditirelo')
    || lowerMessage.includes('consult')
    || lowerMessage.includes('cash flow')
    || lowerMessage.includes('budget')
  ) {
    return botCopy(
      'The main services are financial consulting, payroll management, audit preparation, tax and compliance, internal controls and risk advisory, training, and company secretarial support.',
      'Ditirelo tse dikgolo ke financial consulting, payroll management, boitokisetso jwa audit, lekgetho le compliance, internal controls le risk advisory, thupiso, le company secretarial support.',
    );
  }

  if (lowerMessage.includes('control') || lowerMessage.includes('risk') || lowerMessage.includes('fraud')) {
    return botCopy(
      'For controls and risk, Havillah Harmony can review internal control frameworks, identify financial and operational risks, improve policies, and support fraud risk controls.',
      'Mo controls le risk, Havillah Harmony e ka sekaseka internal control frameworks, ya bona financial le operational risks, ya tokafatsa policies, mme ya thusa ka fraud risk controls.',
    );
  }

  if (lowerMessage.includes('secretarial') || lowerMessage.includes('registration') || lowerMessage.includes('company')) {
    return botCopy(
      'Company secretarial support includes company registrations, statutory amendments, board secretarial services, constitutions, annual returns, and governance documentation.',
      'Company secretarial support e akaretsa company registrations, statutory amendments, board secretarial services, constitutions, annual returns, le governance documentation.',
    );
  }

  if (
    lowerMessage.includes('market')
    || lowerMessage.includes('organisation')
    || lowerMessage.includes('organization')
    || lowerMessage.includes('sme')
    || lowerMessage.includes('ngo')
    || lowerMessage.includes('start')
  ) {
    return botCopy(
      'Havillah Harmony supports SMEs, start-ups, growing businesses, NGOs, donor-funded projects, and corporates seeking payroll support and financial training.',
      'Havillah Harmony e thusa SMEs, start-ups, dikgwebo tse di golang, NGOs, donor-funded projects, le corporates tse di batlang payroll support le financial training.',
    );
  }

  if (
    lowerMessage.includes('contact')
    || lowerMessage.includes('phone')
    || lowerMessage.includes('email')
    || lowerMessage.includes('kgolagano')
    || lowerMessage.includes('dintlha')
  ) {
    return botCopy(
      'You can reach Havillah Harmony at +267 74779425, +267 71573465, or info@havillahharmony.co.bw.',
      'O ka ikgolaganya le Havillah Harmony mo +267 74779425, +267 71573465, kgotsa info@havillahharmony.co.bw.',
    );
  }

  if (lowerMessage.includes('hello') || lowerMessage.includes('hi') || lowerMessage.includes('hey') || lowerMessage.includes('dumela')) {
    return botCopy(
      'Hi there. What would you like help with today? You can choose a topic below or ask about payroll, tax, audits, training, BURS, markets, or contact details.',
      'Dumela. O batla thuso ka eng gompieno? O ka tlhopha topic fa tlase kgotsa wa botsa ka payroll, lekgetho, audits, thupiso, BURS, markets, kgotsa kgolagano.',
    );
  }

  return botCopy(
    "I am not fully sure about that yet. Try one of the quick topics below, or ask about payroll, tax, audits, training, company secretarial services, risk controls, markets, or contact details.",
    'Ga ke ise ke tlhomamise karabo ya seo. Leka topic nngwe fa tlase, kgotsa botsa ka payroll, lekgetho, audits, thupiso, company secretarial services, risk controls, markets, kgotsa dintlha tsa kgolagano.',
    false,
  );
}

function App() {
  const [currentPath, setCurrentPath] = useState(getCurrentPath);
  const [theme, setTheme] = useState('forest');
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [isPageLoading, setIsPageLoading] = useState(true);
  const [showScrollUp, setShowScrollUp] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [mobileMenuPhase, setMobileMenuPhase] = useState('closed');
  const [expandedMobileMenu, setExpandedMobileMenu] = useState(null);
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [chatLanguage, setChatLanguage] = useState('en');
  const [chatInput, setChatInput] = useState('');
  const [isBotThinking, setIsBotThinking] = useState(false);
  const [failedAnswers, setFailedAnswers] = useState(0);
  const [contactForm, setContactForm] = useState(contactFormInitialState);
  const [contactFormStatus, setContactFormStatus] = useState('');
  const loaderTimeoutRef = useRef(null);
  const failedAnswersRef = useRef(0);
  const chatTimersRef = useRef([]);
  const chatEndRef = useRef(null);
  const mobileMenuTransitionRef = useRef(null);
  const [chatMessages, setChatMessages] = useState([
    {
      id: 'bot-welcome',
      sender: 'bot',
      text: formatBotMessage(botCopy(
        'Hi, I am the Havillah assistant. I can help with payroll, tax, audits, training, BURS support, markets, and contact details.',
        'Dumela, ke Havillah assistant. Nka thusa ka payroll, lekgetho, audits, thupiso, BURS support, markets, le dintlha tsa kgolagano.',
      ), 'en'),
      isTyping: false,
    },
  ]);
  const currentPage = routePages[currentPath];
  const isMobileMenuOpen = mobileMenuPhase === 'open';
  const isMobileMenuClosing = mobileMenuPhase === 'closing';
  const isMobileMenuVisible = isMobileMenuOpen || isMobileMenuClosing;
  const currentYear = new Date().getFullYear();
  const pageHero = currentPage === 'home' ? null : pageHeroes[currentPage];

  const searchItems = useMemo(() => {
    const serviceResults = services.map((service) => ({
      title: service.title,
      href: '/services',
      text: `${service.summary} ${service.items.join(' ')}`,
      label: 'Service',
    }));

    return [
      ...pageSummaries.map((page) => ({ ...page, label: 'Page' })),
      ...serviceResults,
    ];
  }, []);

  useEffect(() => {
    const loadingStartedAt = Date.now();

    const finishLoading = () => {
      const remainingTime = Math.max(0, PAGE_LOADER_MIN_MS - (Date.now() - loadingStartedAt));
      loaderTimeoutRef.current = window.setTimeout(() => {
        setIsPageLoading(false);
        loaderTimeoutRef.current = null;
      }, remainingTime);
    };

    if (document.readyState === 'complete') {
      finishLoading();
      return undefined;
    }

    window.addEventListener('load', finishLoading, { once: true });
    return () => window.removeEventListener('load', finishLoading);
  }, []);

  useEffect(() => {
    const handlePopState = () => {
      setCurrentPath(getCurrentPath());
    };

    window.addEventListener('popstate', handlePopState);

    if (hashRoutes[window.location.hash]) {
      window.history.replaceState({}, '', getCurrentPath());
    }

    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  useEffect(() => {
    const updateScrollUpVisibility = () => {
      setShowScrollUp(window.scrollY > SCROLL_UP_REVEAL_OFFSET);
    };

    updateScrollUpVisibility();
    window.addEventListener('scroll', updateScrollUpVisibility, { passive: true });

    return () => window.removeEventListener('scroll', updateScrollUpVisibility);
  }, [currentPath]);

  useEffect(() => {
    const revealTargets = document.querySelectorAll('[data-reveal]');
    const prefersReducedMotion =
      typeof window.matchMedia === 'function'
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (prefersReducedMotion || !('IntersectionObserver' in window)) {
      revealTargets.forEach((target) => target.classList.add('is-visible'));
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        });
      },
      {
        rootMargin: '0px 0px -12% 0px',
        threshold: 0.16,
      },
    );

    revealTargets.forEach((target) => observer.observe(target));

    return () => observer.disconnect();
  }, [currentPage]);

  useEffect(() => () => {
    chatTimersRef.current.forEach((timer) => window.clearTimeout(timer));
    chatTimersRef.current = [];
    if (loaderTimeoutRef.current) {
      window.clearTimeout(loaderTimeoutRef.current);
    }
    if (mobileMenuTransitionRef.current) {
      window.clearTimeout(mobileMenuTransitionRef.current);
    }
  }, []);

  useEffect(() => {
    if (chatEndRef.current && typeof chatEndRef.current.scrollIntoView === 'function') {
      chatEndRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }
  }, [chatMessages, isBotThinking]);

  useEffect(() => {
    if (!isMobileMenuVisible) {
      setExpandedMobileMenu(null);
    }
  }, [isMobileMenuVisible]);

  const scheduleChatTimer = (callback, delay) => {
    const timer = window.setTimeout(() => {
      chatTimersRef.current = chatTimersRef.current.filter((chatTimer) => chatTimer !== timer);
      callback();
    }, delay);
    chatTimersRef.current.push(timer);
    return timer;
  };

  const clearMobileMenuTransition = () => {
    if (mobileMenuTransitionRef.current) {
      window.clearTimeout(mobileMenuTransitionRef.current);
      mobileMenuTransitionRef.current = null;
    }
  };

  const closeMobileMenu = ({ animated = true } = {}) => {
    clearMobileMenuTransition();

    if (!animated) {
      setMobileMenuPhase('closed');
      return;
    }

    setMobileMenuPhase('closing');
    mobileMenuTransitionRef.current = window.setTimeout(() => {
      setMobileMenuPhase('closed');
      mobileMenuTransitionRef.current = null;
    }, 340);
  };

  const toggleMobileMenu = () => {
    if (isMobileMenuOpen) {
      closeMobileMenu();
      return;
    }

    clearMobileMenuTransition();
    setMobileMenuPhase('open');
  };

  const typeBotMessage = (fullText, options = {}) => {
    const messageId = `bot-${Date.now()}-${Math.random().toString(16).slice(2)}`;
    let index = 0;

    setChatMessages((messages) => [
      ...messages,
      {
        id: messageId,
        sender: 'bot',
        text: '',
        isTyping: true,
        isHandoff: options.redirectToWhatsapp,
      },
    ]);

    const typeNextCharacter = () => {
      index += 1;
      setChatMessages((messages) => messages.map((message) => {
        if (message.id !== messageId) {
          return message;
        }

        return {
          ...message,
          text: fullText.slice(0, index),
          isTyping: index < fullText.length,
        };
      }));

      if (index < fullText.length) {
        scheduleChatTimer(typeNextCharacter, TYPEWRITER_SPEED_MS);
        return;
      }

      if (options.redirectToWhatsapp) {
        scheduleChatTimer(() => {
          window.location.href = WHATSAPP_AGENT_URL;
        }, 1200);
      }
    };

    scheduleChatTimer(typeNextCharacter, TYPEWRITER_SPEED_MS);
  };

  const handleSearch = (event) => {
    event.preventDefault();
    const cleanQuery = searchQuery.trim().toLowerCase();

    if (!cleanQuery) {
      setSearchResults([]);
      setIsSearchOpen(false);
      return;
    }

    closeMobileMenu({ animated: false });

    const results = searchItems.filter((item) => {
      const searchable = `${item.title} ${item.text}`.toLowerCase();
      return searchable.includes(cleanQuery);
    });

    setSearchResults(results);
    setIsSearchOpen(true);
  };

  const navigateTo = (path) => {
    const [rawPath, rawHash] = path.split('#');
    const nextPath = normalizePath(rawPath || '/');
    const nextHash = rawHash ? `#${rawHash}` : '';
    const nextUrl = `${nextPath}${nextHash}`;

    if (nextUrl !== `${window.location.pathname}${window.location.hash}`) {
      window.history.pushState({}, '', nextUrl);
      setCurrentPath(nextPath);
    }

    closeMobileMenu({ animated: false });
    window.setTimeout(() => {
      if (rawHash) {
        document.getElementById(rawHash)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        return;
      }

      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 0);
  };

  const handleInternalLinkClick = (event, path) => {
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.altKey || event.ctrlKey || event.shiftKey) {
      return;
    }

    event.preventDefault();
    navigateTo(path);
  };

  const isBotTyping = chatMessages.some((message) => message.isTyping);
  const isBotBusy = isBotThinking || isBotTyping;
  const remainingBotAttempts = Math.max(0, 3 - Math.min(failedAnswers, 3));
  const chatStatusText = chatLanguage === 'en'
    ? (
      remainingBotAttempts > 0
        ? `Need help? WhatsApp support is ready after ${remainingBotAttempts} more ${remainingBotAttempts === 1 ? 'try' : 'tries'}.`
        : 'WhatsApp support is ready now.'
    )
    : (
      remainingBotAttempts > 0
        ? `O tlhoka thuso? WhatsApp support e tla bulega morago ga diteko tse ${remainingBotAttempts}.`
        : 'WhatsApp support e bulegile jaanong.'
    );

  const submitChatMessage = (message) => {
    const cleanInput = message.trim();
    if (!cleanInput || isBotBusy) {
      return;
    }

    setChatMessages((messages) => [
      ...messages,
      {
        id: `user-${Date.now()}`,
        sender: 'user',
        text: cleanInput,
      },
    ]);
    setChatInput('');
    setIsBotThinking(true);
    const responseLanguage = chatLanguage;

    scheduleChatTimer(() => {
      const reply = getBotReply(cleanInput);
      const nextFailedAnswers = reply.known ? 0 : failedAnswersRef.current + 1;
      failedAnswersRef.current = nextFailedAnswers;
      setFailedAnswers(nextFailedAnswers);
      setIsBotThinking(false);

      if (!reply.known && nextFailedAnswers >= 3) {
        typeBotMessage(formatBotMessage(botCopy(
          `I could not answer that confidently after 3 tries. I am connecting you to a WhatsApp live agent now at ${WHATSAPP_AGENT_PHONE}.`,
          `Ga ke a kgona go araba seo sentle morago ga diteko di le 3. Jaanong ke go isa kwa live agent ya WhatsApp mo ${WHATSAPP_AGENT_PHONE}.`,
        ), responseLanguage), { redirectToWhatsapp: true });
        return;
      }

      typeBotMessage(formatBotMessage(reply, responseLanguage));
    }, THINKING_DELAY_MS);
  };

  const handleChatSubmit = (event) => {
    event.preventDefault();
    submitChatMessage(chatInput);
  };

  const handleContactFormChange = (event) => {
    const { name, value } = event.target;

    setContactForm((currentForm) => ({
      ...currentForm,
      [name]: value,
    }));
    setContactFormStatus('');
  };

  const handleContactFormSubmit = (event) => {
    event.preventDefault();

    const whatsappMessage = [
      'Hello Havillah Harmony, I need help from the website contact form.',
      `Name: ${contactForm.name}`,
      `Email: ${contactForm.email}`,
      `Phone: ${contactForm.phone || 'Not provided'}`,
      `Service: ${contactForm.service}`,
      '',
      contactForm.message,
    ].join('\n');
    const whatsappUrl = `https://wa.me/${WHATSAPP_AGENT_PHONE.replace(/\D/g, '')}?text=${encodeURIComponent(whatsappMessage)}`;

    setContactFormStatus('Your WhatsApp message is ready. The team will respond as soon as possible.');

    if (typeof window !== 'undefined') {
      window.open(whatsappUrl, '_blank', 'noopener,noreferrer');
    }
  };

  const renderHeroAction = (action, className) => {
    const opensDirectly =
      action.external || action.href.startsWith('mailto:') || action.href.startsWith('tel:') || action.href.startsWith('http');

    return (
      <a
        className={className}
        href={action.href}
        onClick={opensDirectly ? undefined : (event) => handleInternalLinkClick(event, action.href)}
        target={action.external ? '_blank' : undefined}
        rel={action.external ? 'noreferrer' : undefined}
      >
        <Icon name={action.icon} />
        <span>{action.label}</span>
      </a>
    );
  };

  return (
    <div
      className={`site-shell ${isPageLoading ? 'is-loading' : ''}`}
      data-mode={isDarkMode ? 'dark' : 'light'}
      data-theme={theme}
    >
      {isPageLoading && (
        <div className="page-loader" aria-live="polite" aria-label="Loading Havillah Harmony">
          <div className="page-loader__panel">
            <div className="page-loader__mark" aria-hidden="true">
              <span className="page-loader__ring" />
              <span className="page-loader__ring page-loader__ring--inner" />
            </div>
            <p className="page-loader__eyebrow">Preparing your view</p>
            <strong className="page-loader__title">Havillah Harmony</strong>
          </div>
        </div>
      )}

      <header
        className={[
          'site-header',
          isMobileMenuVisible ? 'is-menu-visible' : '',
          isMobileMenuOpen ? 'is-menu-open' : '',
          isMobileMenuClosing ? 'is-menu-closing' : '',
        ].filter(Boolean).join(' ')}
      >
        <div className="top-header">
          <div className="header-inner">
            <span className="icon-line">
              <Icon name="location" />
              {contact.address}
            </span>
            <div className="top-header__links" aria-label="Social links">
              <a href="https://wa.me/26774779425" target="_blank" rel="noreferrer" aria-label="WhatsApp">
                <Icon name="whatsapp" />
                <span className="visually-hidden">WhatsApp</span>
              </a>
              <a href="/contact" onClick={(event) => handleInternalLinkClick(event, '/contact')} aria-label="LinkedIn">
                <Icon name="linkedin" />
                <span className="visually-hidden">LinkedIn</span>
              </a>
              <a href="/contact" onClick={(event) => handleInternalLinkClick(event, '/contact')} aria-label="Facebook">
                <Icon name="facebook" />
                <span className="visually-hidden">Facebook</span>
              </a>
            </div>
          </div>
        </div>

        <div className="middle-header">
          <div className="header-inner middle-header__layout">
            <a className="brand" href="/" onClick={(event) => handleInternalLinkClick(event, '/')} aria-label="Havillah Harmony home">
              <img src="/logo.png" alt="Havillah Harmony Professional Services" />
            </a>

            <button
              className={`menu-toggle ${isMobileMenuOpen ? 'is-active' : ''}`}
              type="button"
              aria-controls="primary-navigation"
              aria-expanded={isMobileMenuOpen}
              onClick={toggleMobileMenu}
            >
              <span className="menu-toggle__lines" aria-hidden="true">
                <span />
                <span />
                <span />
              </span>
              <span className="visually-hidden">{isMobileMenuOpen ? 'Close menu' : 'Open menu'}</span>
            </button>

            <div className="mobile-menu-search">
              <form className="search-form" onSubmit={handleSearch}>
                <label className="visually-hidden" htmlFor="mobile-site-search">Search Havillah Harmony</label>
                <div className="search-field">
                  <Icon name="search" />
                  <input
                    id="mobile-site-search"
                    type="search"
                    placeholder="Search the site..."
                    value={searchQuery}
                    onChange={(event) => setSearchQuery(event.target.value)}
                  />
                </div>
                <button type="submit" aria-label="Search">
                  <Icon name="search" />
                  <span>Search</span>
                </button>
              </form>
            </div>

            <nav className="primary-nav" id="primary-navigation" aria-label="Primary navigation">
              {navLinks.map((link) => {
                const submenu = mobileMenuSections[link.href];
                const isExpanded = expandedMobileMenu === link.href;
                const submenuId = `mobile-submenu-${link.key || link.label.toLowerCase()}`;

                return (
                  <div
                    className={`primary-nav__item ${submenu ? 'has-submenu' : ''} ${isExpanded ? 'is-expanded' : ''}`}
                    key={link.href}
                  >
                    <a
                      className={`primary-nav__link ${currentPath === link.href ? 'is-active' : ''}`}
                      href={link.href}
                      onClick={(event) => {
                        if (isMobileMenuOpen && submenu) {
                          event.preventDefault();
                          setExpandedMobileMenu((expanded) => (expanded === link.href ? null : link.href));
                          return;
                        }

                        handleInternalLinkClick(event, link.href);
                      }}
                      aria-current={currentPath === link.href ? 'page' : undefined}
                      aria-expanded={submenu && isMobileMenuOpen ? isExpanded : undefined}
                      aria-controls={submenu && isMobileMenuOpen ? submenuId : undefined}
                    >
                      <Icon name={link.icon} />
                      <span>{link.label}</span>
                    </a>
                    {submenu && (
                      <button
                        className="mobile-submenu-toggle"
                        type="button"
                        aria-expanded={isExpanded}
                        aria-controls={submenuId}
                        aria-label={`${isExpanded ? 'Close' : 'Open'} ${link.label} submenu`}
                        onClick={() => setExpandedMobileMenu((expanded) => (expanded === link.href ? null : link.href))}
                      >
                        <Icon name="chevron-down" className="mobile-menu-chevron" />
                      </button>
                    )}
                    {submenu && (
                      <div className="mobile-submenu" id={submenuId} aria-hidden={!isExpanded}>
                        {submenu.map((item) => (
                          <a
                            className="mobile-submenu__link"
                            href={item.href}
                            key={item.href}
                            tabIndex={isExpanded ? undefined : -1}
                            onClick={(event) => handleInternalLinkClick(event, item.href)}
                          >
                            <Icon name={item.icon} />
                            <span>{item.label}</span>
                          </a>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </nav>

            <div className="header-actions">
              <a className="button button--primary" href="/contact" onClick={(event) => handleInternalLinkClick(event, '/contact')}>
                <Icon name="calendar" />
                <span>Book consultation</span>
              </a>
              <button
                className="mode-toggle"
                type="button"
                onClick={() => setIsDarkMode((mode) => !mode)}
                aria-label="Toggle dark and light mode"
              >
                <Icon name={isDarkMode ? 'sun' : 'moon'} />
                <span>{isDarkMode ? 'Light' : 'Dark'}</span>
              </button>
              <div className="theme-dots" aria-label="Theme color switcher">
                {themeOptions.map((option) => (
                  <button
                    key={option.key}
                    className={`theme-dot ${theme === option.key ? 'is-active' : ''}`}
                    type="button"
                    style={{ '--dot-color': option.color }}
                    onClick={() => setTheme(option.key)}
                    aria-label={`Use ${option.name} theme`}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="bottom-header">
          <div className="header-inner bottom-header__layout">
            <form className="search-form" onSubmit={handleSearch}>
              <label className="visually-hidden" htmlFor="site-search">Search Havillah Harmony</label>
              <div className="search-field">
                <Icon name="search" />
                <input
                  id="site-search"
                  type="search"
                  placeholder="Search payroll, services, markets, tax, audit, contact..."
                  value={searchQuery}
                  onChange={(event) => setSearchQuery(event.target.value)}
                />
              </div>
              <button type="submit">
                <Icon name="search" />
                <span>Search</span>
              </button>
            </form>
          </div>
        </div>
      </header>

      <main>
        <section
          className="hero"
          id="home"
          aria-label="Havillah Harmony introduction"
          hidden={currentPage !== 'home'}
        >
          <img
            className="hero__video"
            src="/havillah/hero-building.webp"
            alt=""
            aria-hidden="true"
          />
          <div className="hero__content" data-reveal="hero">
            <p className="eyebrow">Insight Beyond the Numbers</p>
            <h1>Accounting, Payroll, Tax & Advisory Services</h1>
            <p>
              Reliable, ethical, and insightful accounting, payroll, and financial services for businesses,
              organisations, and individuals ready for clarity, structure, and sustainable growth.
            </p>
            <div className="hero__actions">
              <a className="button button--primary" href="/services" onClick={(event) => handleInternalLinkClick(event, '/services')}>
                <Icon name="services" />
                <span>Explore services</span>
              </a>
              <a className="button button--light" href="/contact" onClick={(event) => handleInternalLinkClick(event, '/contact')}>
                <Icon name="contact" />
                <span>Get in touch</span>
              </a>
            </div>
          </div>
        </section>

        <section className="snapshot" aria-label="Profile snapshot" hidden={currentPage !== 'home'}>
          <div className="section-inner snapshot__grid">
            <span data-reveal="lift">
              <Icon name="chart" />
              Professional accounting and advisory firm
            </span>
            <span data-reveal="lift">
              <Icon name="shield" />
              IFRS, payroll, tax, audit readiness, controls, and governance
            </span>
            <span data-reveal="lift">
              <Icon name="location" />
              Gaborone, Botswana
            </span>
          </div>
        </section>

        <section className="section section--tint home-showcase" hidden={currentPage !== 'home'}>
          <div className="section-inner home-showcase__content">
            <div data-reveal="left">
              <p className="eyebrow">Advisory In Motion</p>
              <h2>Numbers, compliance, controls, and decisions moving in one direction.</h2>
              <p>
                Havillah Harmony helps clients move from scattered financial records to a clearer
                working system: reports that make sense, tax obligations that are tracked, controls
                that reduce risk, payroll that runs smoothly, and training that gives teams confidence.
              </p>
              <div className="home-metrics" aria-label="Havillah Harmony strengths">
                {homeMetrics.map((metric) => (
                  <article key={metric.label} data-reveal="card">
                    <strong>{metric.value}</strong>
                    <span>{metric.label}</span>
                  </article>
                ))}
              </div>
            </div>
          </div>
        </section>

        {pageHero && (
          <section className="hero hero--page" aria-label={`${pageHero.eyebrow} introduction`}>
            <img className="hero__image" src={pageHero.image} alt="" aria-hidden="true" />
            <div className="hero__content hero__content--page" data-reveal="hero">
              <p className="eyebrow">{pageHero.eyebrow}</p>
              <h1>{pageHero.title}</h1>
              <p>{pageHero.text}</p>
              <div className="hero__actions">
                {renderHeroAction(pageHero.cta, 'button button--primary')}
                {renderHeroAction(pageHero.secondaryCta, 'button button--light')}
              </div>
              <div className="page-hero__highlights" aria-label="Page highlights">
                {pageHero.highlights.map((highlight) => (
                  <span key={highlight}>{highlight}</span>
                ))}
              </div>
            </div>
          </section>
        )}

        <div className="about-page" id="about" hidden={currentPage !== 'about'}>
        <section className="section split-section" id="about-overview">
          <div className="section-inner split-grid">
            <div data-reveal="left">
              <p className="eyebrow">About Us</p>
              <h2>Financial clarity with thoughtful stewardship.</h2>
              <p>
                Havillah Harmony is a professional accounting and advisory firm providing reliable,
                ethical, and insightful financial services. We partner with businesses,
                organisations, and individuals to bring clarity, structure, and sustainable growth
                to their financial affairs.
              </p>
              <p>
                The approach integrates technical excellence with thoughtful stewardship, enabling
                informed decision-making and long-term value creation.
              </p>
            </div>
            <div className="image-frame" data-reveal="image">
              <img src="/havillah/about-team.webp" alt="Black accountant reviewing financial records at her desk" />
            </div>
          </div>
        </section>

        <section className="section section--tint about-subsection" id="vision-mission" aria-label="About vision and mission">
          <div className="section-inner split-grid split-grid--reversed">
            <div className="image-frame" data-reveal="image">
              <img src="/havillah/vision-mission.webp" alt="Two Black professionals discussing strategy over notes and a laptop" />
            </div>
            <div data-reveal="right">
              <p className="eyebrow">Vision & Mission</p>
              <h2>Trusted clarity for responsible prosperity.</h2>
              <div className="statement-list">
                <article data-reveal="lift">
                  <h3>Vision</h3>
                  <p>
                    To be a trusted source of financial clarity and stewardship, where integrity,
                    excellence, and harmony prevail, empowering individuals and businesses to
                    prosper responsibly.
                  </p>
                </article>
                <article data-reveal="lift">
                  <h3>Mission</h3>
                  <p>
                    To deliver reliable, ethical, and insightful accounting and advisory services
                    that bring clarity, balance, and sustainable growth to our clients' financial affairs.
                  </p>
                </article>
              </div>
            </div>
          </div>
        </section>

        <section className="section about-subsection" id="core-values" aria-label="About core values">
          <div className="section-inner">
            <div className="section-heading" data-reveal="lift">
              <p className="eyebrow">Core Values</p>
              <h2>Integrity, stewardship, excellence, wisdom, harmony, and partnership.</h2>
            </div>
            <div className="values-grid">
              {values.map((value) => (
                <article className="value-card" key={value.title} data-reveal="card">
                  <span className="symbol-token" aria-hidden="true">
                    <Icon name={value.icon} />
                  </span>
                  <h3>{value.title}</h3>
                  <p>{value.text}</p>
                </article>
              ))}
            </div>
          </div>
        </section>
        </div>

        <section className="section section--tint" id="services" hidden={currentPage !== 'services'}>
          <div className="section-inner">
            <div className="section-heading" data-reveal="lift">
              <p className="eyebrow">Our Services</p>
              <h2>Accounting support for reporting, payroll, compliance, controls, training, and governance.</h2>
            </div>
            <div className="service-showcase">
              <article className="service-image-card" data-reveal="image">
                <img src="/havillah/service-consulting.webp" alt="Black financial professionals reviewing advisory reports together" />
                <div>
                  <Icon name="chart" />
                  <span>Consulting & audit readiness</span>
                </div>
              </article>
              <article className="service-image-card" data-reveal="image">
                <img src="/havillah/service-tax.webp" alt="Black tax advisor reviewing compliance paperwork and calculations" />
                <div>
                  <Icon name="tax" />
                  <span>Tax compliance & risk advisory</span>
                </div>
              </article>
              <article className="service-image-card" data-reveal="image">
                <img src="/havillah/service-training.webp" alt="Black finance professionals working through a training session together" />
                <div>
                  <Icon name="training" />
                  <span>Training & company secretarial</span>
                </div>
              </article>
            </div>
            <div className="services-grid">
              {services.map((service) => (
                <article
                  className="service-card"
                  id={`service-${slugifyLabel(service.title)}`}
                  key={service.title}
                  data-reveal="card"
                >
                  <h3>
                    <Icon name={service.icon} />
                    <span>{service.title}</span>
                  </h3>
                  <p>{service.summary}</p>
                  <ul>
                    {service.items.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="section" id="why-choose" hidden={currentPage !== 'home'}>
          <div className="section-inner split-grid">
            <div data-reveal="left">
              <p className="eyebrow">Why Choose Us</p>
              <h2>Clear guidance for confident financial decisions.</h2>
              <div className="reason-list">
                {whyChoose.map((reason) => (
                  <article key={reason.title} data-reveal="lift">
                    <h3>{reason.title}</h3>
                    <p>{reason.text}</p>
                  </article>
                ))}
              </div>
            </div>
            <div className="image-frame" data-reveal="image">
              <img src="/havillah/why-choose-advisory.webp" alt="Black businesswoman working through financial planning with a calculator and notebook" />
            </div>
          </div>
        </section>

        <section className="section section--tint home-process" hidden={currentPage !== 'home'}>
          <div className="section-inner">
            <div className="section-heading" data-reveal="lift">
              <p className="eyebrow">How We Work</p>
              <h2>A practical rhythm for cleaner records, stronger controls, and better decisions.</h2>
            </div>
            <div className="process-grid">
              {advisoryProcess.map((step, index) => (
                <article key={step.title} data-reveal="card">
                  <span>{String(index + 1).padStart(2, '0')}</span>
                  <Icon name={step.icon} />
                  <h3>{step.title}</h3>
                  <p>{step.text}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="section section--tint" id="markets" hidden={currentPage !== 'markets'}>
          <div className="section-inner split-grid split-grid--reversed">
            <div className="image-frame" data-reveal="image">
              <img src="/havillah/target-market.webp" alt="Black business team planning growth around financial reports" />
            </div>
            <div data-reveal="right">
              <p className="eyebrow">Target Market</p>
              <h2>Built for organisations that need clarity, compliance, and reporting confidence.</h2>
              <div className="market-grid">
                {markets.map((market) => (
                  <article className="market-card" key={market} data-reveal="card">
                    <Icon name="markets" />
                    <p>{market}</p>
                  </article>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="section home-cta" hidden={currentPage !== 'home'}>
          <div className="section-inner home-cta__panel" data-reveal="lift">
            <div>
              <p className="eyebrow">Ready For Clarity</p>
              <h2>Bring order to the financial work behind your next decision.</h2>
              <p>
                Get support for accounting records, payroll management, tax compliance, audit preparation,
                internal controls, training, governance, and reporting discipline.
              </p>
            </div>
            <div className="home-cta__actions">
              <a className="button button--primary" href="/contact" onClick={(event) => handleInternalLinkClick(event, '/contact')}>
                <Icon name="whatsapp" />
                <span>Send WhatsApp enquiry</span>
              </a>
              <a className="button button--light" href="/services" onClick={(event) => handleInternalLinkClick(event, '/services')}>
                <Icon name="services" />
                <span>Review services</span>
              </a>
            </div>
          </div>
        </section>

        <section className="section" id="commitment" hidden={currentPage !== 'home'}>
          <div className="section-inner">
            <div className="section-heading" data-reveal="lift">
              <p className="eyebrow">Client Commitment</p>
              <h2>Confidentiality, integrity, transparent communication, and measurable results.</h2>
            </div>
            <div className="commitment-grid">
              {commitments.map((commitment) => (
                <article className="commitment-card" key={commitment} data-reveal="card">
                  <p>{commitment}</p>
                </article>
              ))}
            </div>
            <div className="commitment-image" data-reveal="image">
              <img src="/havillah/client-commitment.webp" alt="Black advisor and client reviewing financial paperwork together" />
            </div>
          </div>
        </section>

        <section className="section contact-section" id="contact" hidden={currentPage !== 'contact'}>
          <div className="section-inner split-grid">
            <div data-reveal="left">
              <p className="eyebrow">Get In Touch</p>
              <h2>Ready for insight beyond the numbers.</h2>
              <p>
                Reach the team in Gaborone for accounting, payroll, tax, audit readiness, training,
                compliance, and advisory support.
              </p>
              <div className="contact-list">
                <div data-reveal="lift">
                  <strong>
                    <Icon name="location" />
                    Address
                  </strong>
                  <span>{contact.address}</span>
                </div>
                <div data-reveal="lift">
                  <strong>
                    <Icon name="phone" />
                    Telephone
                  </strong>
                  {contact.phones.map((phone) => (
                    <a key={phone} href={`tel:${phone.replaceAll(' ', '')}`}>{phone}</a>
                  ))}
                </div>
                <div data-reveal="lift">
                  <strong>
                    <Icon name="email" />
                    Email
                  </strong>
                  {contact.emails.map((email) => (
                    <a key={email} href={`mailto:${email}`}>{email}</a>
                  ))}
                </div>
              </div>
            </div>
            <div data-reveal="right">
              <form className="contact-form" onSubmit={handleContactFormSubmit}>
                <div className="contact-form__intro">
                  <p className="eyebrow">Send An Enquiry</p>
                  <h3>Tell us what you need help with.</h3>
                  <p>Share a few details and the team will follow up with the right guidance.</p>
                </div>
                <div className="contact-form__grid">
                  <label className="contact-field" htmlFor="contact-name">
                    <span>Full name</span>
                    <input
                      id="contact-name"
                      name="name"
                      type="text"
                      autoComplete="name"
                      value={contactForm.name}
                      onChange={handleContactFormChange}
                      required
                    />
                  </label>
                  <label className="contact-field" htmlFor="contact-email">
                    <span>Email address</span>
                    <input
                      id="contact-email"
                      name="email"
                      type="email"
                      autoComplete="email"
                      value={contactForm.email}
                      onChange={handleContactFormChange}
                      required
                    />
                  </label>
                  <label className="contact-field" htmlFor="contact-phone">
                    <span>Phone number</span>
                    <input
                      id="contact-phone"
                      name="phone"
                      type="tel"
                      autoComplete="tel"
                      value={contactForm.phone}
                      onChange={handleContactFormChange}
                    />
                  </label>
                  <label className="contact-field" htmlFor="contact-service">
                    <span>Service needed</span>
                    <select
                      id="contact-service"
                      name="service"
                      value={contactForm.service}
                      onChange={handleContactFormChange}
                    >
                      <option>General enquiry</option>
                      {services.map((service) => (
                        <option key={service.title}>{service.title}</option>
                      ))}
                    </select>
                  </label>
                  <label className="contact-field contact-field--wide" htmlFor="contact-message">
                    <span>Message</span>
                    <textarea
                      id="contact-message"
                      name="message"
                      value={contactForm.message}
                      onChange={handleContactFormChange}
                      rows="6"
                      required
                    />
                  </label>
                </div>
                <button className="contact-form__button" type="submit">
                  <Icon name="whatsapp" />
                  <span>Send via WhatsApp</span>
                </button>
                {contactFormStatus && (
                  <p className="contact-form__status" role="status">{contactFormStatus}</p>
                )}
              </form>
            </div>
          </div>
        </section>

        <section className="section section--tint contact-map-section" hidden={currentPage !== 'contact'}>
          <div className="section-inner">
            <div className="section-heading" data-reveal="lift">
              <p className="eyebrow">Visit Us</p>
              <h2>Find Havillah Harmony in Gaborone.</h2>
            </div>
            <div className="contact-map" data-reveal="image">
              <iframe
                title="Google map to Havillah Harmony in Gaborone"
                src={contactMapSrc}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                allowFullScreen
              />
            </div>
            <a className="contact-map__link" href={contactMapLink} target="_blank" rel="noreferrer">
              <Icon name="location" />
              <span>Open directions in Google Maps</span>
            </a>
          </div>
        </section>
      </main>

      <footer className="site-footer" aria-labelledby="footer-title">
        <div className="section-inner footer-main">
          <div className="footer-brand">
            <a className="footer-logo" href="/" onClick={(event) => handleInternalLinkClick(event, '/')} aria-label="Havillah Harmony home">
              <img src="/logo.png" alt="Havillah Harmony Professional Services" />
            </a>
            <h2 id="footer-title">Insight beyond the numbers.</h2>
            <p>
              Professional accounting, payroll, tax, audit readiness, training, and advisory support
              for organisations that need clarity and confident decisions.
            </p>
            <div className="footer-actions">
              <a href="/contact" onClick={(event) => handleInternalLinkClick(event, '/contact')}>
                <Icon name="calendar" />
                <span>Book consultation</span>
              </a>
              <a href={WHATSAPP_AGENT_URL} target="_blank" rel="noreferrer">
                <Icon name="whatsapp" />
                <span>WhatsApp support</span>
              </a>
            </div>
          </div>

          <nav className="footer-column" aria-label="Footer pages">
            <h3>Explore</h3>
            {navLinks.map((link) => (
              <a key={link.href} href={link.href} onClick={(event) => handleInternalLinkClick(event, link.href)}>
                <Icon name={link.icon} />
                <span>{link.label}</span>
              </a>
            ))}
          </nav>

          <nav className="footer-column footer-column--wide" aria-label="Footer services">
            <h3>Services</h3>
            {services.map((service) => (
              <a key={service.title} href="/services" onClick={(event) => handleInternalLinkClick(event, '/services')}>
                <Icon name={service.icon} />
                <span>{service.title}</span>
              </a>
            ))}
          </nav>

          <div className="footer-column footer-contact">
            <h3>Contact</h3>
            <a href={`tel:${contact.phones[0].replaceAll(' ', '')}`}>
              <Icon name="phone" />
              <span>{contact.phones[0]}</span>
            </a>
            <a href={`tel:${contact.phones[1].replaceAll(' ', '')}`}>
              <Icon name="phone" />
              <span>{contact.phones[1]}</span>
            </a>
            <a href={`mailto:${contact.emails[2]}`}>
              <Icon name="email" />
              <span>{contact.emails[2]}</span>
            </a>
            <p>
              <Icon name="location" />
              <span>{contact.address}</span>
            </p>
            <div className="footer-socials" aria-label="Social links">
              <a href="https://wa.me/26774779425" target="_blank" rel="noreferrer" aria-label="WhatsApp">
                <Icon name="whatsapp" />
              </a>
              <a href="/contact" onClick={(event) => handleInternalLinkClick(event, '/contact')} aria-label="LinkedIn">
                <Icon name="linkedin" />
              </a>
              <a href="/contact" onClick={(event) => handleInternalLinkClick(event, '/contact')} aria-label="Facebook">
                <Icon name="facebook" />
              </a>
            </div>
          </div>
        </div>

        <div className="section-inner footer-bottom">
          <span>&copy; {currentYear} Havillah Harmony Professional Services</span>
          <span>Accounting, Payroll, Tax & Advisory Services</span>
          <span>Gaborone, Botswana</span>
        </div>
      </footer>

      <button
        className={`scroll-up ${showScrollUp ? 'is-visible' : ''}`}
        type="button"
        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        aria-label="Scroll to top"
      >
        <Icon name="arrow-up" />
      </button>

      <div className={`chat-widget ${isChatOpen ? 'is-open' : ''}`}>
        {isChatOpen && (
          <section className="chat-panel" aria-label="Havillah Harmony assistance chat">
            <div className="chat-panel__header">
              <div>
                <strong>
                  <Icon name="chat" />
                  Havillah Assistant
                </strong>
                <span className="chat-panel__language-status">
                  {chatLanguage === 'en' ? 'Replying in English' : 'E araba ka Setswana'}
                </span>
              </div>
              <div className="chat-panel__actions">
                <a className="chat-panel__agent" href={WHATSAPP_AGENT_URL} target="_blank" rel="noreferrer">
                  <Icon name="whatsapp" />
                  Live agent
                </a>
                <button className="chat-panel__close" type="button" onClick={() => setIsChatOpen(false)} aria-label="Close chat">
                  <Icon name="close" />
                  <span>Close</span>
                </button>
              </div>
            </div>
            <div className="chat-language-switch" aria-label="Choose chat language">
              <button
                type="button"
                className={chatLanguage === 'en' ? 'is-active' : ''}
                aria-pressed={chatLanguage === 'en'}
                onClick={() => setChatLanguage('en')}
              >
                English
              </button>
              <button
                type="button"
                className={chatLanguage === 'tn' ? 'is-active' : ''}
                aria-pressed={chatLanguage === 'tn'}
                onClick={() => setChatLanguage('tn')}
              >
                Setswana
              </button>
            </div>
            <div className="chat-panel__messages">
              {chatMessages.map((message, index) => (
                <p
                  key={message.id || `${message.sender}-${index}`}
                  className={`chat-message chat-message--${message.sender} ${message.isTyping ? 'is-typing' : ''}`}
                >
                  {message.text}
                </p>
              ))}
              {isBotThinking && (
                <div className="chat-thinking" aria-live="polite">
                  <span />
                  <span />
                  <span />
                  <strong>{chatLanguage === 'en' ? 'Thinking...' : 'Ke a akanya...'}</strong>
                </div>
              )}
              {!isBotBusy && (
                <div className="chat-quick-actions" aria-label={chatLanguage === 'en' ? 'Quick chat topics' : 'Ditlhopho tsa chat'}>
                  {QUICK_CHAT_ACTIONS.map((action) => (
                    <button
                      key={action.id}
                      type="button"
                      className="chat-quick-action"
                      onClick={() => submitChatMessage(action.prompt[chatLanguage])}
                    >
                      <Icon name={action.icon} />
                      <span>{action.label[chatLanguage]}</span>
                    </button>
                  ))}
                </div>
              )}
              <div ref={chatEndRef} aria-hidden="true" />
            </div>
            <form className="chat-form" onSubmit={handleChatSubmit}>
              <label className="visually-hidden" htmlFor="chat-input">Ask for assistance</label>
              <input
                id="chat-input"
                type="text"
                value={chatInput}
                onChange={(event) => setChatInput(event.target.value)}
                placeholder={
                  isBotBusy
                    ? (chatLanguage === 'en' ? 'Assistant is replying...' : 'Assistant e a araba...')
                    : (chatLanguage === 'en' ? 'Ask about payroll, tax, audits, BURS...' : 'Botsa ka payroll, lekgetho, audits, BURS...')
                }
                disabled={isBotBusy}
              />
              <button type="submit" disabled={isBotBusy}>
                <Icon name="contact" />
                <span>Send</span>
              </button>
            </form>
            <div className="chat-panel__status">
              <span>{chatStatusText}</span>
              <a href={WHATSAPP_AGENT_URL} target="_blank" rel="noreferrer">{WHATSAPP_AGENT_PHONE}</a>
            </div>
          </section>
        )}
        <button className="chat-launcher" type="button" onClick={() => setIsChatOpen((open) => !open)} aria-label="Click to chat">
          <Icon name="chat" />
          <span>Click to chat</span>
        </button>
      </div>

      {isSearchOpen && (
        <div className="search-modal" role="dialog" aria-modal="true" aria-labelledby="search-modal-title">
          <div className="search-modal__panel">
            <div className="search-modal__header">
              <div>
                <p className="eyebrow">Search Results</p>
                <h2 id="search-modal-title">{searchQuery.trim() ? `Results for "${searchQuery.trim()}"` : 'Popular pages'}</h2>
              </div>
              <button type="button" onClick={() => setIsSearchOpen(false)}>
                <Icon name="close" />
                <span>Close</span>
              </button>
            </div>
            {searchResults.length > 0 ? (
              <div className="search-results">
                {searchResults.map((result) => (
                  <a
                    key={`${result.label}-${result.title}`}
                    href={result.href}
                    onClick={(event) => {
                      handleInternalLinkClick(event, result.href);
                      setIsSearchOpen(false);
                    }}
                  >
                    <span>
                      <Icon name={result.label === 'Service' ? 'services' : 'document'} />
                      {result.label}
                    </span>
                    <strong>{result.title}</strong>
                    <small>{result.text}</small>
                  </a>
                ))}
              </div>
            ) : (
              <p className="empty-search">No matching results yet. Try payroll, audit, tax, training, markets, or contact.</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default App;
